import clsx from 'clsx';
import {
  type CSSProperties,
  forwardRef,
  isValidElement,
  type ReactNode,
  Children,
} from 'react';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { devLog } from '../../core/utils/logging.js';
import { useContainerColoring } from '../../layouts/container/Container.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { containerColorsCSS } from '../../styles/container.sty.js';
import { SharedProgressBarPropsContext } from './contexts/SharedProgressBarPropsContext.js';
import { useProgressAriaProps } from './hooks/useProgressAriaProps.js';
import {
  progressBarFillCSS,
  progressCSS,
  progressRoot,
} from './ProgressBar.sty.js';
import { ProgressBarIcon } from './ProgressBarIcon.js';
import { ProgressBarLabel } from './ProgressBarLabel.js';
import { ProgressBarValue } from './ProgressBarValue.js';

const COMPONENT_NAME = 'ProgressBar';

/**
 * Accepted properties for ProgressBar.
 * @public
 */
export interface ProgressBarProps
  extends
    WithChildren,
    DOMProps,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * The current value.
   * @defaultValue 'indeterminate'
   */
  value?: number | 'indeterminate';
  /**
   * The minimum allowed value.
   * @defaultValue 0
   */
  min?: number;
  /**
   * The maximum allowed value.
   * @defaultValue 100
   */
  max?: number;
  /**
   * Controls the density of the rendering.
   * @defaultValue 'default'
   */
  density?: 'default' | 'condensed';
  /**
   * Controls the color of the progress.
   * @defaultValue 'primary' or a container's color.
   */
  color?: 'neutral' | 'primary' | 'success' | 'warning' | 'critical';
  /**
   * The aria-valuetext attribute defines the human-readable text alternative of aria-valuenow for a range widget.
   * By default, the value will be given as a percentage. If the value is not a percentage, you should provide aria-valuetext.
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-valuetext
   * @defaultValue the value of the progress as percentage.
   */
  'aria-valuetext'?: string;
}

type ProgressBarSlots = {
  value?: ReactNode;
  label?: ReactNode;
  icon?: ReactNode;
};

function getProgressBarSlots(children: ReactNode) {
  const slots: ProgressBarSlots = {
    value: null,
    label: null,
    icon: null,
  };

  Children.forEach(children, (child) => {
    if (isValidElement(child)) {
      if (isSlot(child, ProgressBarLabel)) {
        slots.label = child;
      } else if (isSlot(child, ProgressBarIcon)) {
        slots.icon = child;
      } else {
        slots.value = child;
      }
    } else {
      if (!slots.value) {
        slots.value = child;
      }
    }
  });

  return slots;
}

const _ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (props, forwardedRef) => {
    const {
      value: originalValue,
      max = 100,
      min = 0,
      density = 'default',
      color,
      children,
      id: propId,
      'aria-valuetext': ariaValuetext,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    }: ProgressBarProps = props;

    const containerColoring = useContainerColoring();

    // for the color in the ProgressCircle the fallback logic is a bit different - usually
    // the fallback from the container is "neutral" - but in the case of the progress circle the default
    // fallback color is "primary"
    const fallbackColor = containerColoring.default
      ? 'primary'
      : containerColoring.color;

    const derivedColor = color ?? fallbackColor;
    const onAccent = containerColoring.variant === 'accent';

    const slots = getProgressBarSlots(children);
    const { value, isIndeterminate, progressAriaProps } = useProgressAriaProps(
      originalValue,
      min,
      max,
      ariaValuetext,
    );

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const generatedId = useId();
    const id = propId ?? generatedId;

    if (
      !slots.label &&
      !ariaLabelingProps['aria-label'] &&
      !ariaLabelingProps['aria-labelledby']
    ) {
      devLog(
        'If no label is provided please make sure to provide an aria label on your progressCircle element.',
      );
    }

    const progressStyles: CSSProperties = {};
    if (!isIndeterminate && value !== undefined) {
      const percentage = (value - min) / (max - min);
      progressStyles['width'] = `${Math.round(percentage * 100)}%`;
    }

    return (
      <SharedProgressBarPropsContext.Provider value={{ id, density }}>
        <Flex
          ref={forwardedRef}
          flexDirection="column"
          gap={6}
          width="100%"
          className={clsx(
            containerColorsCSS({
              color: derivedColor,
              variant: containerColoring.variant,
            }),
            progressRoot({
              onAccent,
              color: derivedColor,
            }),
            consumerClassName,
          )}
          style={consumerStyle}
          data-testid={dataTestId}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          {...behavioralTrackingProps}
          {...forwardProps}
          data-dt-component={COMPONENT_NAME}
        >
          <Flex justifyContent="space-between" gap={8}>
            {density === 'condensed' ? (
              <Flex
                justifyContent="space-between"
                alignItems="flex-end"
                flex={1}
                gap={16}
                minWidth={0}
              >
                {slots.label}
                {slots.value}
              </Flex>
            ) : (
              slots.label
            )}
            {slots.icon}
          </Flex>
          <div
            className={progressCSS({
              size: density,
            })}
            id={id}
            {...progressAriaProps}
            {...ariaLabelingProps}
          >
            <div
              className={clsx(
                progressBarFillCSS({
                  indeterminate: isIndeterminate,
                }),
              )}
              style={progressStyles}
            />
          </div>
          {density === 'default' && slots.value}
        </Flex>
      </SharedProgressBarPropsContext.Provider>
    );
  },
);

(_ProgressBar as typeof _ProgressBar & { displayName: string }).displayName =
  COMPONENT_NAME;

/**
 * Use the `ProgressBar` to indicate an ongoing saving or uploading process in a specific part of the page.
 * @public
 */
export const ProgressBar = Object.assign(_ProgressBar, {
  Value: ProgressBarValue,
  Label: ProgressBarLabel,
  Icon: ProgressBarIcon,
});
